import { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { InputNumber } from 'components/UI/InputNumber/InputNumber';
import { InfoPopover } from 'components/UI/InfoPopover/InfoPopover';
import { Button } from 'components/UI/Button/Button';
import { DropdownWithMenu } from 'components/UI/DropdownWithMenu/DropdownWithMenu';
import { requestSeats } from 'services/RequestSeatsService';
import {
  getCurrentSeat,
  getDropDownOptions,
  getTotalSeatsSelected,
  calculateInputOperands,
  calculateMaxTicketsReached,
  addErrorMessageToPage,
} from './best-available-seats-utils';

import './BestAvailableSeats.scss';

export const BestAvailableSeats = (props) => {
  const {
    SectionZone,
    PriceTypes,
    MaxTicketsPerOrder,
    ProdSeasonId,
    PerformanceId,
    SpecialRequirementsText,
    TicketFeeStandardDisclaimer,
  } = props;
  // Default to Zone 0 when available, or the first Zone
  const [chosenZoneId, setChosenZoneId] = useState(
    SectionZone.find((zone) => zone.ZoneId === 0)?.ZoneId
    || SectionZone[0]?.ZoneId
    || 0
  );
  const [seatsSelected, setSeatsSelected] = useState([]);
  const [availablePriceTypes, setAvailablePriceTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalTicketCount, setTotalTicketCount] = useState(0);
  const ticketZone = SectionZone[0];

  const getAvailablePriceTypes = () => {
    // By default Zone 0 or 'All Sections' are shown
    if (chosenZoneId === 0) {
      setAvailablePriceTypes(PriceTypes);
      // Else if a specific Section/Zone is chosen
    } else {
      const chosenZoneObj = SectionZone.find((zone) => zone.ZoneId === chosenZoneId);
      setAvailablePriceTypes(chosenZoneObj?.ZonePriceTypePriceRange);
    }
  };

  const zoneDropdownUpdateHandler = (zoneId) => {
    if (zoneId !== null) {
      setChosenZoneId(zoneId);
      setSeatsSelected([]);
      setTotalTicketCount(0);
    }
  };

  // Whenever a new Zone is chosen
  useEffect(() => {
    getAvailablePriceTypes();
  }, [chosenZoneId]);

  const addSelectedSeats = (inputId, inputValue) => {
    let updatedSeatsArr = [];
    const seatAlreadySelected = seatsSelected.some((seatType) => seatType.PriceTypeId === inputId);

    if (seatAlreadySelected) {
      updatedSeatsArr = seatsSelected.map((seatType) => {
        const matchingSeatId = seatType.PriceTypeId === inputId;
        return {
          ...seatType,
          NumSeats: matchingSeatId ? inputValue : seatType.NumSeats,
        };
      });
    } else {
      updatedSeatsArr = [
        ...seatsSelected,
        {
          PriceTypeId: inputId,
          NumSeats: inputValue,
        },
      ];
    }

    setSeatsSelected(updatedSeatsArr);
  };

  const removeSelectedSeats = (inputId, inputValue) => {
    let updatedSeatsArr = [];
    updatedSeatsArr = seatsSelected.map((seatType) => {
      const matchingSeatId = seatType.PriceTypeId === inputId;
      return {
        ...seatType,
        NumSeats: matchingSeatId ? inputValue : seatType.NumSeats,
      };
    });

    setSeatsSelected(updatedSeatsArr);
  };

  const incrementChangeHandler = (inputId, inputValue, operandValue) => {
    addSelectedSeats(inputId, inputValue);
    setTotalTicketCount((prevCount) => prevCount + operandValue);
  };

  const decrementChangeHandler = (inputId, inputValue, operandValue) => {
    removeSelectedSeats(inputId, inputValue);
    setTotalTicketCount((prevCount) => prevCount - operandValue);
  };

  const findSeatsButtonHandler = async () => {
    setIsLoading(true);

    const requestSeatsPayload = {
      ZoneId: chosenZoneId,
      PriceTypes: seatsSelected,
      ProdSeasonId,
      PerformanceId,
    };

    try {
      window.trackEvent('add_to_cart', { bestAvailable: requestSeatsPayload });
      const response = await requestSeats(requestSeatsPayload);

      if (response.Redirect) {
        window.location.href = response.RedirectUrl;
      } else {
        setIsLoading(false);
        setChosenZoneId(0);
        addErrorMessageToPage(response.Error);
      }
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      setChosenZoneId(0);
      addErrorMessageToPage('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="best-available-seats container">
      <div className="row">
        <div className="col-sm-6 col-md-5">
          <div className="form-group select-seat-preferences">
            <DropdownWithMenu
              singleColumn
              hideDefaultItem
              dropdownChangeHandler={zoneDropdownUpdateHandler}
              defaultChosenValue={`${ticketZone.Description} (${ticketZone.PriceRange})`}
              menuData={getDropDownOptions(SectionZone)}
              extendClass="best-available-seats__dropdown"
            />
          </div>
        </div>
      </div>
      <div className="ticket-types-container">
        {availablePriceTypes?.map((priceType, index) => {
          const { PriceTypeId } = priceType;
          const priceTypeTitle = priceType.AliasDescription || priceType.Description;
          const totalSeatsSelected = getTotalSeatsSelected(seatsSelected);
          const currentSeat = getCurrentSeat(seatsSelected, PriceTypeId);
          const { operandDecrement, operandIncrement } = calculateInputOperands(priceType, currentSeat);
          const maxTicketsReached = calculateMaxTicketsReached(
            currentSeat,
            priceType,
            operandIncrement,
            totalSeatsSelected,
            MaxTicketsPerOrder
          );

          return (
            <div className={`row ${index % 2 === 0 ? 'bg-alt' : ''}`} key={priceType.Description}>
              <div className="col-xs-5">
                <strong className="ticket-type">{priceTypeTitle}</strong>
                <br />
                {priceType.PriceRange}
              </div>
              <div className="col-xs-1 col-sm-4">
                {priceType.Message && (
                  <InfoPopover
                    infoContent={priceType.Message}
                    infoLinkLabel={priceType.ShortMessage}
                  />
                )}
              </div>
              <div className="col-xs-6 col-sm-3">
                <div className="form-group pull-right">
                  <InputNumber
                    id={PriceTypeId}
                    decrementChangeHandler={decrementChangeHandler}
                    incrementChangeHandler={incrementChangeHandler}
                    disableIncrement={maxTicketsReached}
                    resetInputValue={chosenZoneId}
                    operandDecrement={operandDecrement}
                    operandIncrement={operandIncrement}
                    inputIsReadOnly
                  />
                </div>
              </div>
            </div>
          );
        })}
        {TicketFeeStandardDisclaimer && (
          <p>
            <small className="disclaim">{TicketFeeStandardDisclaimer}</small>
          </p>
        )}
      </div>
      <hr />

      <div className="row">
        {SpecialRequirementsText ? (
          <div className="col-sm-9">
            <InfoPopover
              infoContent={SpecialRequirementsText}
              infoLinkLabel="Have a special requirement?"
              displayLabelOnMobile
              extendClass="footer-info-popover"
            />
          </div>
        ) : (
          <div className="col-sm-9">&nbsp;</div>
        )}

        <div className="col-sm-3">
          <Button
            extendClass="btn btn-primary btn-cta pull-right"
            disabled={totalTicketCount === 0 || isLoading}
            onClick={findSeatsButtonHandler}
          >
            {isLoading ? 'Finding Seats...' : 'Find Seats'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const BestAvailableSeatsSC = (props) => {
  const { sitecoreData } = props;
  return <BestAvailableSeats {...sitecoreData} />;
};

BestAvailableSeatsSC.propTypes = {
  sitecoreData: PropTypes.object,
};

BestAvailableSeats.propTypes = {
  SectionZone: PropTypes.arrayOf(PropTypes.object),
  PriceTypes: PropTypes.arrayOf(PropTypes.object),
  MaxTicketsPerOrder: PropTypes.number,
  ProdSeasonId: PropTypes.number,
  PerformanceId: PropTypes.number,
  SpecialRequirementsText: PropTypes.string,
  TicketFeeStandardDisclaimer: PropTypes.string,
};
